import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
} from "react";
import { useSessionData } from "./SessionDataContext";

interface AirportContextType {
  hasConcourse: boolean;
  hasCustoms: boolean;
  hasBaggage: boolean;
  hasAPM: boolean;
  aiChatEnabled: boolean;
  retailAiChatEnabled: boolean;
  marketDemandEnabled: boolean;
  retailFlightsEnabled: boolean;
}

const AirportContext = createContext<AirportContextType | null>(null);

export const AirportProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const sessionData = useSessionData();

  // Derive airport configuration from session data
  const airportConfig = {
    hasConcourse: sessionData?.airport_config.has_concourse ?? false,
    // Check if customs exists in the airport setup
    hasCustoms: (sessionData?.airport.customs?.length ?? 0) > 0,
    // Check if baggage halls exist in the airport setup
    hasBaggage: (sessionData?.airport.baggage_halls?.length ?? 0) > 0,
    // Check if APM exists in the airport setup
    hasAPM:
      sessionData?.airport.apm !== null &&
      sessionData?.airport.apm !== undefined,
    // Flags are already gated server-side in auth.py:
    //   airport.ai_chat_enabled AND airport.is_ai_chat_allowed(email)
    // Empty ai_chat_beta_emails on the airport = open to all users.
    aiChatEnabled: sessionData?.airport_config.ai_chat_enabled ?? false,
    retailAiChatEnabled:
      sessionData?.airport_config.retail_ai_chat_enabled ?? false,
    marketDemandEnabled:
      sessionData?.airport_config.market_demand_enabled ?? false,
    retailFlightsEnabled:
      sessionData?.airport_config.retail_flights_enabled ?? false,
  };

  return (
    <AirportContext.Provider value={airportConfig}>
      {children}
    </AirportContext.Provider>
  );
};

export const useAirport = () => {
  const context = useContext(AirportContext);
  if (!context) {
    throw new Error("useAirport must be used within an AirportProvider");
  }
  return context;
};
